/**
 * Watches the current user's pending invitations and shows a toast the
 * first time we see each one in this browser tab. Rendered once near the
 * root by AppShell.
 *
 * "Seen" is tracked in sessionStorage so the toast doesn't re-trigger on
 * every route change but does come back next session — until the user
 * accepts or declines, the invitation is still actionable.
 */

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { t } from "../i18n";
import { trpc } from "../lib/trpc";
import { useToast } from "./ui";

const STORAGE_KEY = "fellesen.seenInvitationToasts";
const PENDING_INVITATION_KEY = "fellesen.pendingInvitation";

function loadSeen(): Set<string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
}

function persistSeen(seen: Set<string>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
  } catch {
    // sessionStorage unavailable — drop silently.
  }
}

export function InvitationToastBridge() {
  const navigate = useNavigate();
  const { show } = useToast();
  const utils = trpc.useUtils();
  const invitations = trpc.invitation.listMine.useQuery();
  const seenRef = useRef<Set<string>>(loadSeen());
  const acceptInvitation = trpc.invitation.accept.useMutation({
    onSuccess: (data) => {
      utils.invitation.listMine.invalidate();
      utils.household.list.invalidate();
      navigate({
        to: "/households/$householdId/$tab",
        params: { householdId: data.householdId, tab: "settlements" },
      });
    },
  });

  useEffect(() => {
    const list = invitations.data;
    if (!list || list.length === 0) return;

    // Deep-link follow-through: if the user just signed in via Google after
    // clicking an invitation link, /register stashed the invitation ID for
    // us to pick up here. Accept it and route straight to the household —
    // skip the toast entirely.
    let stashed: string | null = null;
    try {
      stashed = sessionStorage.getItem(PENDING_INVITATION_KEY);
    } catch {
      // sessionStorage unavailable — fall through to toast behavior.
    }
    if (stashed) {
      try {
        sessionStorage.removeItem(PENDING_INVITATION_KEY);
      } catch {
        // ignore
      }
      const match = list.find((inv) => inv.id === stashed);
      if (match && !acceptInvitation.isPending) {
        acceptInvitation.mutate({ invitationId: match.id });
        return;
      }
    }

    const seen = seenRef.current;
    const fresh = list.filter((inv) => !seen.has(inv.id));
    if (fresh.length === 0) return;

    const first = fresh[0];
    if (!first) return;
    const title =
      fresh.length === 1
        ? t.invitations.toast.titleSingle(first.householdName)
        : t.invitations.toast.titleMultiple(fresh.length);
    const description =
      fresh.length === 1
        ? first.invitedByDisplayName
          ? t.invitations.toast.descriptionFrom(first.invitedByDisplayName)
          : undefined
        : t.invitations.toast.descriptionMultiple;
    show({
      id: "invitation-toast",
      tone: "info",
      title,
      description,
      duration: null,
      dismissLabel: t.invitations.toast.dismissLabel,
      action: {
        label: t.invitations.toast.viewAction,
        onClick: () => navigate({ to: "/" }),
      },
    });

    for (const inv of fresh) seen.add(inv.id);
    persistSeen(seen);
  }, [invitations.data, show, navigate, acceptInvitation]);

  return null;
}
